import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ProgressBar } from "react-bootstrap";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const PHRASES = [
  { upto: 15, text: "Warming up the engines" },
  { upto: 35, text: "Scanning across platforms" },
  { upto: 55, text: "Analyzing performance trends" },
  { upto: 75, text: "Crunching numbers & insights" },
  { upto: 90, text: "Almost there… finalizing results" },
  { upto: 100, text: "Wrapping things up" },
];

const LONG_WAIT_DELAY = 30000; // 30 seconds

const EMAIL_LONG_WAIT_MESSAGE =
  "This is taking longer than usual — feel free to keep exploring the platform. We'll email you the results as soon as they're ready.";
const NO_EMAIL_LONG_WAIT_MESSAGE =
  "We're fetching your results — we'll let you know once they're ready. Meanwhile, feel free to explore the rest of the platform and enjoy the other benefits.";
const STILL_PROCESSING_MESSAGE =
  "This is taking longer than expected — we're still working on it in the background. We'll notify you as soon as it's ready.";

const SearchProgressModal = ({
  show,
  onHide,
  onCancel,
  progress,
  title = "Searching",
  notifyByEmail = false,
  // `progress` only tracks whether the *request* stopped loading, not whether
  // it actually turned up something - a poll that gave up, an empty result
  // set, or a timeout all stop "loading" without the search being genuinely
  // done. Callers that can tell the difference should pass this so the
  // "results are ready" toast isn't shown for a search that quietly failed.
  hasResults = true,
}) => {
  const activePhrase = useMemo(() => {
    return (
      PHRASES.find((p) => progress <= p.upto)?.text ||
      "Finalizing results…"
    );
  }, [progress]);

  // The long-wait notice is shown inside the modal body instead of a toast, for both
  // email and non-email flows.
  const [longWaitMessage, setLongWaitMessage] = useState(null);
  // Tracks whether we've already told the user "we'll let you know" for the current
  // request, so the "results ready" toast only fires when that promise was made
  // (non-email flow only — email flow's results arrive via email, not in-app).
  const longWaitShownRef = useRef(false);
  const prevProgressRef = useRef(progress);

  const handleClose = () => {
    if (onCancel) onCancel();
    else if (onHide) onHide();
  };

  useEffect(() => {
    if (!show) return undefined;

    // A fresh request just started showing the modal — reset state from any prior run.
    setLongWaitMessage(null);
    longWaitShownRef.current = false;

    const timer = setTimeout(() => {
      longWaitShownRef.current = !notifyByEmail;
      setLongWaitMessage(notifyByEmail ? EMAIL_LONG_WAIT_MESSAGE : NO_EMAIL_LONG_WAIT_MESSAGE);
    }, LONG_WAIT_DELAY);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, notifyByEmail]);

  // The backend keeps processing regardless of the modal's visibility — `progress`
  // reaches 100 the moment the caller's `loading` flag goes false. Once that happens,
  // and only if we'd already told the user we'd let them know, surface a toast —
  // even if the user has already closed the modal by then.
  useEffect(() => {
    const justCompleted = prevProgressRef.current < 100 && progress >= 100;
    if (justCompleted && !notifyByEmail && longWaitShownRef.current) {
      if (hasResults) {
        toastr.success(`${title} is complete — your results are ready!`, "All Done");
      } else {
        // Loading stopped, but there's nothing to show for it - the poll gave
        // up, the result set came back empty, or it timed out. Say that
        // honestly instead of claiming a completion that didn't happen -
        // warning, not info, to match the in-modal long-wait notice's tone.
        toastr.warning(STILL_PROCESSING_MESSAGE, "Still Working On It");
      }
      longWaitShownRef.current = false;
    }
    prevProgressRef.current = progress;
  }, [progress, notifyByEmail, title, hasResults]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
      style={{ zIndex: 9999 }}
      backdropClassName="search-progress-backdrop"
    >
      <Modal.Header
        style={{
          background: "linear-gradient(135deg, #1E1F2B, #21222D)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Modal.Title
          style={{
            color: "#FFFFFF",
            fontWeight: 600,
            letterSpacing: "0.3px",
          }}
        >
          🔍 {title}
        </Modal.Title>
        <button
          onClick={handleClose}
          aria-label="Cancel search"
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(255,255,255,0.5)",
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer",
            padding: "2px 6px",
            borderRadius: 4,
            transition: "color 0.2s ease, background 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#fff";
            e.currentTarget.style.background = "rgba(255,255,255,0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "rgba(255,255,255,0.5)";
            e.currentTarget.style.background = "transparent";
          }}
        >
          ✕
        </button>
      </Modal.Header>

      <Modal.Body
        style={{
          backgroundColor: "#21222D",
          color: "#D2D2D2",
          padding: 28,
        }}
      >
        {/* Animated phrase */}
        <div
          key={activePhrase}
          style={{
            textAlign: "center",
            fontSize: 15,
            fontWeight: 500,
            color: "#E6E6EB",
            marginBottom: 20,
            animation: "fadeSlideIn 400ms ease",
          }}
        >
          {activePhrase}
        </div>

        {/* Long-wait notice — shown in the modal instead of a toast. This is
            "still working, taking a while" news, not a failure, so it gets a
            warning-amber treatment rather than the brand red/pink, which
            reads as an error at a glance. */}
        {longWaitMessage && (
          <div
            style={{
              marginBottom: 18,
              padding: "12px 14px",
              background: "rgba(245,158,11,0.1)",
              border: "1px solid rgba(245,158,11,0.3)",
              borderRadius: 8,
              color: "#E6E6EB",
              fontSize: 13,
              lineHeight: 1.5,
              textAlign: "center",
              animation: "fadeSlideIn 400ms ease",
            }}
          >
            {longWaitMessage}
          </div>
        )}

        {/* Progress Bar */}
        <ProgressBar
          now={progress}
          animated
          variant="pink"
          style={{
            height: 12,
            borderRadius: 10,
            backgroundColor: "#F6255A",
            overflow: "hidden",
          }}
        />

        {/* Progress meta */}
        <div
          style={{
            marginTop: 12,
            textAlign: "center",
            fontSize: 12,
            color: "#9FA0B5",
          }}
        >
          {progress}% completed
        </div>

        {/* Subtle reassurance */}
        <div
          style={{
            marginTop: 18,
            textAlign: "center",
            fontSize: 12,
            color: "#7F8096",
          }}
        >
          Good things take a moment — thanks for your patience
        </div>

        {/* Local animation */}
        <style>{`
          @keyframes fadeSlideIn {
            from {
              opacity: 0;
              transform: translateY(6px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .search-progress-backdrop {
            background-color: rgba(0, 0, 0, 0.95) !important;
            z-index: 9998 !important;
          }
        `}</style>
      </Modal.Body>
    </Modal>
  );
};

export default SearchProgressModal;
